import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="objectives--aggregate-report-refresh"
export default class extends Controller {
  static targets = ["frame"]
  static values = {
    objectiveId: Number
  }

  connect() {
    console.log("Aggregate report refresh controller connected for objective", this.objectiveIdValue)

    // Initialize throttle queue (Story 7-6 performance optimization)
    this.refreshQueue = []
    this.throttleDelay = 1000 // 1 second between refreshes
    this.isProcessing = false

    // Listen for custom event dispatched by Turbo Stream broadcast
    this.boundHandleRefresh = this.handleRefresh.bind(this)
    document.addEventListener('objective-aggregate-refresh', this.boundHandleRefresh)
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener('objective-aggregate-refresh', this.boundHandleRefresh)

    // Flush any pending refreshes before disconnect (Story 7-6 performance optimization)
    if (this.refreshQueue.length > 0) {
      console.log("Flushing pending aggregate refreshes on disconnect")
      this.refresh()
      this.refreshQueue = []
    }
  }

  // Handle custom event from Turbo Stream broadcast
  handleRefresh(event) {
    const { objectiveId } = event.detail

    // Only refresh if this is the correct objective
    if (objectiveId !== this.objectiveIdValue) {
      return
    }

    console.log("Queueing aggregate report refresh for objective", objectiveId)

    // Add to throttle queue instead of refreshing immediately (Story 7-6 performance optimization)
    this.queueRefresh(objectiveId)
  }

  // Queue refresh with throttling to prevent excessive updates (Story 7-6 performance optimization)
  queueRefresh(objectiveId) {
    // Add to queue
    this.refreshQueue.push({ objectiveId, timestamp: Date.now() })

    // Process queue if not already processing
    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  async processQueue() {
    if (this.refreshQueue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true

    // Always use the most recent refresh request (discard older ones in queue)
    const latestRefresh = this.refreshQueue[this.refreshQueue.length - 1]
    this.refreshQueue = [] // Clear queue - we're processing the latest

    console.log("Processing aggregate report refresh for objective", latestRefresh.objectiveId)

    // Execute the refresh
    this.refresh()

    // Wait for throttle delay before processing next update
    await new Promise(resolve => setTimeout(resolve, this.throttleDelay))

    // Continue processing if more refreshes were queued during delay
    this.processQueue()
  }

  // Refresh the aggregate report by updating the turbo-frame src
  refresh() {
    if (!this.hasFrameTarget) {
      console.warn("No frame target found for aggregate report refresh")
      return
    }

    const frame = this.frameTarget
    const currentSrc = frame.src || frame.getAttribute("src")

    if (!currentSrc) {
      console.warn("No src attribute found on aggregate report frame")
      return
    }

    console.log("Refreshing aggregate report frame:", currentSrc)

    // Force reload by updating src attribute
    // Turbo Frame will fetch fresh data from the server with proper instance variables
    frame.src = currentSrc
    frame.reload()
  }
}
